/**
 * 🌟 Arkturian Settings Page
 * Demonstrates the YAML-based settings system in action
 * 
 * Philosophy: This page is a manifestation of consciousness-driven configuration
 */

import React from 'react';
import { Settings, Sparkles, Cpu } from 'lucide-react';
import GenericSettingsPanel from '@/components/ui/GenericSettingsPanel';
import { useSettingsStore } from '@/store/useSettingsStore';
import { createMockSettingsSchema } from '@/config/yaml-loader';
import Panel from '@/components/ui/Panel';

const SettingsPage: React.FC = () => {
  const { schema, loadSchema } = useSettingsStore();

  // Initialize with mock schema on mount
  React.useEffect(() => {
    if (!schema) {
      const mockSchema = createMockSettingsSchema();
      loadSchema(mockSchema);
    }
  }, [schema, loadSchema]);

  const titleStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#dbeafe',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  };

  const descriptionStyle: React.CSSProperties = {
    color: '#94a3b8',
    fontSize: '1rem',
    lineHeight: '1.6',
    marginBottom: '2rem',
  };

  const infoBoxStyle: React.CSSProperties = {
    padding: '1rem',
    background: 'rgba(96, 165, 250, 0.1)',
    border: '1px solid rgba(96, 165, 250, 0.2)',
    borderRadius: '0.5rem',
    marginBottom: '2rem',
  };

  return (
    <div style={{ 
      height: '100vh', 
      overflow: 'hidden', 
      display: 'flex', 
      flexDirection: 'column',
      padding: '2rem 1rem',
      paddingBottom: '6rem' // Space for dock
    }}>
      <div style={{ width: '100%', maxWidth: '48rem', margin: '0 auto', flex: 1, overflow: 'auto' }} className="scrollable-content">
        <h1 style={titleStyle}>
          <Settings size={32} />
          Arkturian Settings System
        </h1>
        
        <p style={descriptionStyle}>
          Experience the power of YAML-driven configuration. Every setting below is automatically 
          generated from a schema definition, with built-in animation capabilities and persistent storage.
        </p>

        {/* System Info */}
        <Panel padding="md" border={true} style={{ marginBottom: '2rem' }}>
          <div style={infoBoxStyle}>
            <h3 style={{ 
              color: '#60a5fa', 
              marginBottom: '0.5rem', 
              fontSize: '1rem', 
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Sparkles size={16} />
              System Architecture
            </h3>
            <ul style={{ 
              color: '#94a3b8', 
              fontSize: '0.875rem', 
              lineHeight: '1.5',
              paddingLeft: '1.5rem',
              margin: 0
            }}>
              <li><strong>YAML → Schema:</strong> Configuration drives UI generation</li>
              <li><strong>Animation Engine:</strong> Real-time value interpolation with multiple easing modes</li>
              <li><strong>State Management:</strong> Zustand-powered with persistence</li>
              <li><strong>Type Safety:</strong> Full TypeScript integration</li>
            </ul>
          </div>

          <div style={infoBoxStyle}>
            <h3 style={{ 
              color: '#10b981', 
              marginBottom: '0.5rem', 
              fontSize: '1rem', 
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Cpu size={16} />
              Features Demonstrated
            </h3>
            <ul style={{ 
              color: '#94a3b8', 
              fontSize: '0.875rem', 
              lineHeight: '1.5',
              paddingLeft: '1.5rem',
              margin: 0
            }}>
              <li><strong>Animated Sliders:</strong> Click the play button to see sine/cosine animations</li>
              <li><strong>Color Pickers:</strong> Real-time color selection with hex input</li>
              <li><strong>Smart Toggles:</strong> Elegant switch components</li>
              <li><strong>Select Dropdowns:</strong> Multi-option consciousness modes</li>
              <li><strong>Export/Import:</strong> Save and restore your configuration</li>
            </ul>
          </div>
        </Panel>

        {/* Main Settings Panel */}
        <GenericSettingsPanel schema={schema || undefined} />
      </div>
    </div>
  );
};

export default SettingsPage; 